import { useNavigate } from "react-router-dom";
import { Loader2, RotateCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { NotificationRow } from "./NotificationRow";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationDropdown,
} from "@/hooks/useNotifications";

/**
 * D-043 S2 — bell dropdown body (TZ §6.7: чотири стани — list / empty /
 * loading / error; без empty-стану — біла панель).
 */
export function NotificationDropdown({
  open,
  unreadCount,
  onClose,
}: {
  open: boolean;
  unreadCount: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isPending, isError, refetch, isRefetching } = useNotificationDropdown(open);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const goToCenter = () => {
    navigate("/notifications");
    onClose();
  };

  return (
    <div className="flex w-[380px] max-w-[92vw] flex-col">
      <div className="flex items-center justify-between border-b border-border px-3.5 py-3">
        <span className="text-[13px] font-bold text-foreground">Сповіщення</span>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={isPending || markAll.isPending}
            className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
          >
            {markAll.isPending && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
            Позначити всі прочитаними
          </button>
        )}
      </div>

      <div className="max-h-[340px] overflow-y-auto">
        {isPending && (
          <div className="space-y-3 p-3.5" aria-label="Завантаження сповіщень">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-[30px] w-[30px] rounded-badge" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {isError && !isPending && (
          <div className="px-4 py-8 text-center">
            <p className="mb-3 text-sm text-muted-foreground">Не вдалося завантажити</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex items-center gap-1.5 rounded-badge border border-primary px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5"
            >
              <RotateCw className={isRefetching ? "h-3 w-3 animate-spin" : "h-3 w-3"} />
              Повторити
            </button>
          </div>
        )}

        {data && !isError && data.list.length === 0 && (
          <div className="px-4 py-8 text-center">
            <p className="mb-3 text-sm text-muted-foreground">Нових сповіщень немає</p>
            <button
              type="button"
              onClick={goToCenter}
              className="text-xs font-semibold text-primary hover:underline"
            >
              Усі сповіщення
            </button>
          </div>
        )}

        {data && !isError && data.list.length > 0 && (
          <div role="list" aria-label="Останні сповіщення">
            {data.list.map((row) => (
              <NotificationRow
                key={row.id}
                row={row}
                size="sm"
                onActivate={onClose}
                onMarkRead={(id) => markRead.mutate(id)}
              />
            ))}
          </div>
        )}
      </div>

      {data && data.list.length > 0 && (
        <div className="border-t border-border py-2.5 text-center">
          <button
            type="button"
            onClick={goToCenter}
            className="text-xs font-semibold text-primary hover:underline"
          >
            Усі сповіщення →
          </button>
        </div>
      )}
    </div>
  );
}
